/**
 * Link between the mounted Raw panel and the `raw.*` commands (W2-05): the panel registers a controller while it is
 * mounted, so "Apply raw JSON" (Ctrl+Enter), "Revert" and friends act on the text being edited.
 */

import type { AppServices } from '../../app/registry/types';
import type { EncodeStyle } from '../../formats/rpe/json';
import { currentFragment, docIrSource, encodeFragment, rawTargets, type IrSource, type RawTarget } from './rawEdit';

export interface RawController {
  /** True while the text differs from what export writes. */
  readonly dirty: boolean;
  readonly target: RawTarget | null;
  apply(): void;
  revert(): void;
}

let controller: RawController | null = null;

export function setRawController(c: RawController | null): void {
  controller = c;
}

export function rawController(): RawController | null {
  return controller;
}

/** Settings keys of the Raw panel (both off by default: indented JSON, RPE-style numbers). */
export const RAW_COMPACT_KEY = 'raw.compact';
export const RAW_SHORTEST_KEY = 'raw.shortestNumbers';

/** Layout and number style of the Raw panel (settings `ext.raw.compact`, `ext.raw.shortestNumbers`). */
export function styleOf(ext: Readonly<Record<string, unknown>>): EncodeStyle {
  return { pretty: ext[RAW_COMPACT_KEY] !== true, floatStyle: ext[RAW_SHORTEST_KEY] === true ? 'shortest' : 'rpe' };
}

export function rawStyle(services: Pick<AppServices, 'settings'>): EncodeStyle {
  return styleOf(services.settings.value.ext);
}

/** The engine's IR when an engine runs, else the document's own lowering. */
export function irSourceOf(services: Pick<AppServices, 'engine' | 'store'>): IrSource | null {
  const store = services.store;
  if (store === null) return null;
  const engine = services.engine;
  if (engine !== null) return { irLine: (line) => engine.irLine(line) };
  return docIrSource(store.doc, store.idx);
}

/** The first Raw target of the current selection and its exported JSON (null when there is nothing to show). */
export function selectionJson(
  services: Pick<AppServices, 'engine' | 'store' | 'editor' | 'settings'>,
): { readonly target: RawTarget; readonly text: string } | null {
  const store = services.store;
  const src = irSourceOf(services);
  if (store === null || src === null) return null;
  const st = services.editor.getState();
  for (const target of rawTargets(store.doc, store.idx, st.selection, st.activeLine, st.focusedColumn)) {
    const frag = currentFragment(target, src);
    if (frag !== null) return { target, text: encodeFragment(frag, rawStyle(services)) };
  }
  return null;
}
